import type { ApiResponse } from './../common/types/api-response';
import { type Alert } from './types/alert';
import { httpService } from './../common/http.service';
import logger from '@/services/logger';
import type { UpdateAlertStatusPayload } from './types/update-alert-status-payload';

export async function getAlerts() {
  try {
    const res = await httpService.get<ApiResponse<Alert[]>>('alert');
    return res.data.data || [];
  } catch (err) {
    logger.error({
      message: '[AlertAPI] - has issues with getAlerts',
      data: {},
      error: err as Error,
    });
    throw err;
  }
}

export async function muteUserAlerts(id: string) {
  try {
    const res = await httpService.post<ApiResponse<undefined>>(`alert/mute`, { userToMuteId: id });
    return res.data;
  } catch (err) {
    logger.error({
      message: '[AlertAPI] - has issues with muteUserAlerts',
      data: { id },
      error: err as Error,
    });
    throw err;
  }
}

export async function unmuteUser(id: string) {
  try {
    const res = await httpService.post<ApiResponse<undefined>>(`alert/unmute`, { userToUnmuteId: id });
    return res.data;
  } catch (err) {
    logger.error({
      message: '[AlertAPI] - has issues with unmuteUser',
      data: { id },
      error: err as Error,
    });
    throw err;
  }
}

export type MutedUser = {
  id: string;
  name: string;
  avatarUrl: string | null;
};

export function mapMutedUsersPayload(payload: unknown): MutedUser[] {
  if (Array.isArray(payload)) {
    return payload;
  }
  if (payload && typeof payload === 'object' && 'data' in payload) {
    const inner = (payload as { data: unknown }).data;
    if (Array.isArray(inner)) {
      return inner;
    }
  }
  return [];
}

export async function getMutedUsers() {
  try {
    const res = await httpService.get<ApiResponse<MutedUser[]>>(`alert/muted-users`);
    return mapMutedUsersPayload(res.data);
  } catch (err) {
    logger.error({
      message: '[AlertAPI] - has issues with getMutedUsers',
      data: {},
      error: err as Error,
    });
    throw err;
  }
}

export async function removeAlert(id: string) {
  try {
    const res = await httpService.delete<ApiResponse<undefined>>(`alert/${id}`);
    return res.data;
  } catch (err) {
    logger.error({
      message: '[AlertAPI] - has issues with removeAlert',
      data: { id },
      error: err as Error,
    });
    throw err;
  }
}

export async function readAlerts(alertIds: string[]) {
  try {
    const res = await httpService.put<ApiResponse<undefined>>(`alert/read`, { alertIds });
    return res.data;
  } catch (err) {
    logger.error({
      message: '[AlertAPI] - has issues with readAlerts',
      data: { alertIds },
      error: err as Error,
    });
    throw err;
  }
}

export async function updateAlertStatus(payload: UpdateAlertStatusPayload) {
  try {
    const res = await httpService.put<ApiResponse<undefined>>(`alert/status`, payload);
    return res.data;
  } catch (err) {
    logger.error({
      message: '[AlertAPI] - has issues with updateAlertStatus',
      data: { payload },
      error: err as Error,
    });
    throw err;
  }
}
